import { HttpClient } from "@angular/common/http";
import { inject, Injectable, signal } from "@angular/core";
import { toObservable } from "@angular/core/rxjs-interop";
import { TranslateService } from "@ngx-translate/core";
import { TOKEN_PROGRAM_ID, getAssociatedTokenAddressSync } from '@solana/spl-token';
import { Config, estimateGas, getAccount, getBalance, getPublicClient, readContracts } from "@wagmi/core";
import { debounceTime, filter } from "rxjs";
import { Address, encodeFunctionData, formatEther, formatUnits, parseEther } from "viem";

import { environment } from "../../../environments/environment";
import { CHAINS } from "../../shared/constants/app-enums";
import { bscUsdcContract, bscUsdtContract, ethUsdcContract, ethUsdtContract, getChainData, getPresaleContract, isEmptyObject, isSolanaActivated, multicallBscContract, multicallEthContract, presaleContractBsc, presaleContractEth, skipIndexUpdate, stakeContract } from "../../shared/constants/wallet-connect.const";
import { IBalnceDataEVM, IBalnceDataSOL, ITokenomics } from "../../shared/interfaces/wallet.interface";
import { AnalyticsService } from "../analytics.service";
import { PopupService } from "../popup.service";
import { SolanaInitialisationService } from "../sol-services/solana-initialise.service";
import { ReownService } from "./reown.service";
import { StakingService } from "../evm-services/staking.service";
import { bsc, bscTestnet, mainnet, sepolia } from "viem/chains";


/**
 * Core data service managing the global presale state across multiple chains (EVM and Solana).
 * Uses Angular Signals for reactive state management and RxJS for external event streams.
 */
@Injectable({
    providedIn: 'root',
})
export class PresaleDataService {
    private refreshRate: number = 10000;
    private oldWalletAddress: string = "";
    private isSolIntilised: boolean = false;
    private isSoldOut: boolean = false;
    gasFeesEth = 0;
    gasFeesBsc = 0;
    refreshIdGas!: NodeJS.Timeout;
    refreshRateGas: number = 10000;

    public refreshId!: NodeJS.Timeout;
    public wagmiConfig!: Config;
    public walletAddress!: string;
    public walletAddressSOL!: string;
    public localStep: number = 0;
    public chainId!: number; // don't change this untill it is coming from reown service
    public balanceDataEVM: IBalnceDataEVM = {} as IBalnceDataEVM;
    public balanceDataSOl: IBalnceDataSOL = {} as IBalnceDataSOL;
    /** Full presale data (balances, tokens sold, etc.) used by claim and staking views. */
    private readonly _balanceData = signal<{ sol: IBalnceDataSOL; evm: IBalnceDataEVM }>({ evm: {} as IBalnceDataEVM, sol: {} as IBalnceDataSOL });
    readonly balanceData = this._balanceData.asReadonly();

    /** Subset of balance data specifically for the buy widget to optimize change detection. */
    private readonly _widgetBalanceData = signal<{ sol: IBalnceDataSOL; evm: IBalnceDataEVM }>({ evm: {} as IBalnceDataEVM, sol: {} as IBalnceDataSOL });
    readonly widgetBalanceData = this._widgetBalanceData.asReadonly();

    /** Reactive signal for the current wallet address. */
    private readonly _walletAddressSignal = signal<string>('');
    readonly walletAddressSignal = this._walletAddressSignal.asReadonly();
    public tokenomics: ITokenomics[] = [];
    public userStaked: number = 0
    public stakeApy: number = 0
    public activeChain: string = CHAINS.EVM;
    public saleToken: Address = "" as Address;
    public connectedWalletName: string = "";
    // public isReferalAvailable: boolean = false;


    private readonly reownService = inject(ReownService);
    private readonly solService = inject(SolanaInitialisationService);
    private readonly analyticsService = inject(AnalyticsService);
    private readonly popupService = inject(PopupService);
    private readonly translateService = inject(TranslateService);
    private readonly http = inject(HttpClient);
    private readonly stakeService = inject(StakingService);

    constructor() {
        // debounceTime(200) prevents rapid re-fetches when Reown emits multiple events on connect
        toObservable(this.reownService.connectionItems).pipe(
            debounceTime(200)
        ).subscribe((item) => {
            if (!this.wagmiConfig && item.wagmiConfig) {
                this.wagmiConfig = item.wagmiConfig;
                clearTimeout(this.refreshId);
                this.getPresalesData(this.isSolIntilised);
                this.isSolIntilised = false;
            }

            if (item.activeChain) this.activeChain = item.activeChain;
            
            const hasNewEvmAddress = item.walletAddressEVM && item.walletAddressEVM !== this.walletAddress;
            const hasNewSolAddress = item.walletAddressSOL && item.walletAddressSOL !== this.walletAddressSOL;

            if (item.wagmiConfig && hasNewEvmAddress) {
                this.walletAddress = item.walletAddressEVM as string;
                this._walletAddressSignal.set(this.walletAddress);
                this.connectedWalletName = this.reownService.connectedWalletName;
                clearTimeout(this.refreshId);
                this.setData();
                this.getPresalesData(true);
            }

            if (!isEmptyObject(item) && hasNewSolAddress) {
                this.walletAddressSOL = item.walletAddressSOL as string;
                this._walletAddressSignal.set(this.walletAddressSOL);
                this.connectedWalletName = this.reownService.connectedWalletName;
                clearTimeout(this.refreshId);
                this.getPresalesData(true);
            }
        });

        toObservable(this.reownService.walletDisconnected).pipe(
            filter(Boolean)
        ).subscribe(() => {
            clearTimeout(this.refreshId);
            this.walletAddress = '';
            this.walletAddressSOL = '';
            this._walletAddressSignal.set('');
            this.connectedWalletName = '';
            this.analyticsService.referUrl = '';
            
            // Re-initialize to guest state
            this.balanceDataEVM = {} as IBalnceDataEVM;
            this.balanceDataSOl = {} as IBalnceDataSOL;
            this._balanceData.set({ evm: this.balanceDataEVM, sol: this.balanceDataSOl });
            this._widgetBalanceData.set({ evm: this.balanceDataEVM, sol: this.balanceDataSOl });

            this.setData();
            // Trigger immediate refresh for guest data (total raised, etc.)
            this.getPresalesData(this.isSolIntilised);
        });

        toObservable(this.solService.solInitialised).subscribe((item) => {
            if (item && this.wagmiConfig?.storage) {
                this.isSolIntilised = item;
                clearTimeout(this.refreshId);
                this.getPresalesData(true);
            }
        });
    }

    updateCurrentNetwork(): void {
        if (this.activeChain === CHAINS.EVM) {
            this._walletAddressSignal.set(this.walletAddress);
        } else this._walletAddressSignal.set(this.walletAddressSOL);
    }

    /**
     * Orchestrates the fetching of presale data from both EVM and Solana chains.
     * Updates reactive signals and sets up a polling interval for real-time updates.
     * @param isSolInilized Whether the Solana program is initialized and ready for data fetching.
     */
    async getPresalesData(isSolInilized = false) {
        if (!this.wagmiConfig) return;

        let _solBalanceData: IBalnceDataSOL = {} as IBalnceDataSOL;
        await this.prepareInitialWidgetLoadItems(isSolInilized);
        const currentStep = this.balanceDataEVM.currentStep;

        const ethBalanceData = await this.prepareETHContractData();
        const bscBalanceData = await this.prepareBSCContractData();
        // this.isReferalAvailable = this.chainId == environment.eth.chainIdInt ? ethBalanceData.isReferalAvailable || false : bscBalanceData.isReferalAvailable || false;



        // /* assigning the value of total token purchased by the current user */
        this.balanceDataEVM.ethClaimable = ethBalanceData.ethClaimable;
        this.balanceDataEVM.bscClaimable = bscBalanceData.bscClaimable;
        this.balanceDataEVM.totalClaimable = (this.balanceDataEVM.ethClaimable || 0) + (this.balanceDataEVM.bscClaimable || 0);
        this.balanceDataEVM = { ...this.balanceDataEVM, ...ethBalanceData, ...bscBalanceData }

        if (isSolInilized) {
            _solBalanceData = await this.getPresaleDataSOL();
            const data = await this.getSolprice();
            const noOfTokenForOneSOl = data.Price / +this.tokenomics[currentStep]?.tokenUSDT;
            _solBalanceData.oneSol = 1 / noOfTokenForOneSOl;
            const availableToken = this.tokenomics[currentStep]?.maxToken - _solBalanceData.totalTokensSoldSOL;
            _solBalanceData.maxTokensToBuySOL = availableToken > _solBalanceData.tokenBuyLimit ? _solBalanceData.tokenBuyLimit : availableToken;
            _solBalanceData.solUsdt = +this.tokenomics[currentStep]?.tokenUSDT * Math.pow(10, 6) || 0;
        }
        this.balanceDataSOl = _solBalanceData

        this._balanceData.set({ evm: this.balanceDataEVM, sol: this.balanceDataSOl });
        this._widgetBalanceData.set({ evm: this.balanceDataEVM, sol: this.balanceDataSOl });

        if (this.reownService.sendVwoData && (this.walletAddress || this.walletAddressSOL)) {
            this.analyticsService.vwoData('walletConnectionSuccessful', {
                walletAddress: this.walletAddress || this.walletAddressSOL,
                connectionType: this.connectedWalletName,
                ethBalance: this.balanceDataEVM.nativeBalETH || 0,
                bnbBalance: this.balanceDataEVM.nativeBalBSC || 0,
                solBalance: this.balanceDataSOl?.nativeBalSol || 0,
                stablecoinBalanceEth: (this.balanceDataEVM.usdtBalETH || 0) + (this.balanceDataEVM.usdcBalETH || 0),
                stablecoinBalanceBsc: (this.balanceDataEVM.usdtBalBSC || 0) + (this.balanceDataEVM.usdcBalBSC || 0),
                stablecoinBalanceSol: (this.balanceDataSOl?.usdtBalSol || 0) + (this.balanceDataSOl?.usdcBalSol || 0),
            });
            this.reownService.sendVwoData = false;
        }

        if (this.walletAddress && this.isSoldOut) this.getMetaData();
        if (this.refreshId) clearTimeout(this.refreshId);
        this.refreshId = setTimeout(() => this.getPresalesData(isSolInilized), this.refreshRate); // Call the self method on each 5secs interval
    }

    // usd raised, tokenssold, tokenomics preparation for initial widget loading
    async prepareInitialWidgetLoadItems(solInit = false) {
        const smartContract = {
            contracts: [

                {
                    ...presaleContractEth,
                    functionName: 'currentStep',
                    chainId: environment.eth.chainIdInt, // 0
                    args: [],
                },
                {
                    ...presaleContractEth,
                    functionName: 'usdRaised', // this method is returning the total USDT raised via BSC token.
                    chainId: environment.eth.chainIdInt, // 1
                    args: [],
                },
                {
                    ...presaleContractBsc,
                    functionName: 'usdRaised', // this method is returning the total USDT raised via BSC token.
                    chainId: environment.bsc.chainIdInt, // 2
                    args: [],
                },
                {
                    ...presaleContractEth, // 3
                    functionName: 'trackRemainingTokens',
                    chainId: environment.eth.chainIdInt,
                    args: [],
                },
                {
                    ...presaleContractBsc, // 4
                    functionName: 'trackRemainingTokens',
                    chainId: environment.bsc.chainIdInt,
                    args: [],
                },
                {
                    ...presaleContractEth,
                    functionName: 'roundDetails',
                    chainId: environment.eth.chainIdInt, // 5 ROUND END DATES
                    args: [2],
                },
                {
                    ...presaleContractEth,
                    functionName: 'roundDetails',
                    chainId: environment.eth.chainIdInt, //6 MAX TOKENS
                    args: [0],
                },
                {
                    ...presaleContractEth,
                    functionName: 'roundDetails',
                    chainId: environment.eth.chainIdInt, //7 TOKEN USDT
                    args: [1],
                },
                {
                    ...presaleContractEth,
                    functionName: 'totalTokensSold', // this method is returning the no of tokens sold via BSC
                    chainId: environment.eth.chainIdInt, // 8
                    args: [],
                },
                {
                    ...presaleContractBsc,
                    functionName: 'totalTokensSold', // this method is returning the no of tokens sold via BSC
                    chainId: environment.bsc.chainIdInt, // 9
                    args: [],
                },
            ],
        } as const;

        /**
         * Above contracts are going to be read.
         */
        const _presaleData: any = await readContracts(this.wagmiConfig, smartContract);



        const currentStep = Number(_presaleData[0].result);
        if (currentStep >= this.localStep) {
            this.localStep = currentStep;
            this.balanceDataEVM.currentStep = currentStep;
        } else {
            this.balanceDataEVM.currentStep = this.localStep;
        }
        this.balanceDataEVM.usdRaisedETH = +formatEther(_presaleData[1].result || 0)
        this.balanceDataEVM.usdRaisedBSC = +formatEther(_presaleData[2].result || 0);
        this.balanceDataEVM.totalUsdRaised = this.balanceDataEVM.usdRaisedETH + this.balanceDataEVM.usdRaisedBSC
        this.balanceDataEVM.ethTokensSold = Number(_presaleData[8].result);
        this.balanceDataEVM.bscTokensSold = Number(_presaleData[9].result);
        this.balanceDataEVM.totalTokensSold = this.balanceDataEVM.ethTokensSold + this.balanceDataEVM.bscTokensSold;
        let solUnsoldArr = []
        if (solInit) {
            const stateInfo = await this.solService.program.account.stateInfo.fetch(this.solService.stateInfoAddress[0]); // this.stateInfoAddress[0] = Public Key for State Info
            this.balanceDataSOl.totalTokensSoldSOL = Number(stateInfo.tokensSold) + 1;
            this.balanceDataSOl.usdRaised = Number(stateInfo.totalUsdRaised) / 1000000 + 1;
            const soldArray = stateInfo?.tokensLeftUnsold?.toString()?.split(',');
            const skipIndexSol: Array<any> = [];
            solUnsoldArr = skipIndexUpdate(
                soldArray.map((val: any) => Number(val)),
                skipIndexSol,
            );
        }

        this.tokenomics = await this.prepareTokenomics(_presaleData, currentStep, solUnsoldArr);
        this.balanceDataEVM.totalUsdRaised = (this.balanceDataEVM.usdRaisedETH || 0) + (this.balanceDataEVM.usdRaisedBSC || 0);

        this._widgetBalanceData.set({ evm: this.balanceDataEVM, sol: this.balanceDataSOl });
    }

    async prepareETHContractData(): Promise<IBalnceDataEVM> {
        const ethBalanceData: IBalnceDataEVM = {} as IBalnceDataEVM
        const smartContract = {
            contracts: [
                // =========== ETH Contract ==============
                {
                    ...presaleContractEth,
                    functionName: 'ethBuyHelper', // this method is returning the amount of bnb required for 1 app token.
                    chainId: environment.eth.chainIdInt,  //0
                    args: [1],
                },
                {
                    ...presaleContractEth,
                    functionName: 'usdtBuyHelper', // this method is returning the amount of usdt required for 1 app token.
                    chainId: environment.eth.chainIdInt, //1
                    args: [1],
                },
                {
                    ...presaleContractEth, // 4 MAx token allowed to buy
                    functionName: 'maxTokensToBuy', //2
                    chainId: environment.eth.chainIdInt,
                    args: [],
                },
                {
                    ...presaleContractEth,
                    functionName: 'userDeposits', // this method is returning the total token purchased by current user via ETH network.
                    chainId: environment.eth.chainIdInt, //3
                    args: [this.walletAddress || '0x0000000000000000000000000000000000000000'],
                },
                {
                    ...multicallEthContract,
                    functionName: 'getEthBalance', // this method is returning the eth balance of user via multicall contract.
                    chainId: environment.eth.chainIdInt, //4
                    args: [this.walletAddress || '0x0000000000000000000000000000000000000000'],
                },
                {
                    ...ethUsdtContract,
                    functionName: 'balanceOf', // this method is returning the usdt balance on ETH network.
                    chainId: environment.eth.chainIdInt,  //5
                    args: [this.walletAddress || '0x0000000000000000000000000000000000000000'],
                },
                {
                    ...presaleContractEth, // To get the token address dynamically
                    functionName: 'saleToken', //6
                    chainId: environment.eth.chainIdInt,
                    args: [],
                },
                {
                    ...ethUsdcContract,
                    functionName: 'balanceOf', // this method is returning the usdc balance on ETH network.
                    chainId: environment.eth.chainIdInt, //7
                    args: [this.walletAddress || '0x0000000000000000000000000000000000000000'],
                },
                {
                    ...stakeContract,
                    functionName: 'poolStakers',
                    chainId: environment.eth.chainIdInt, // 8
                    args: [this.walletAddress || '0x0000000000000000000000000000000000000000'],
                },
                {
                    ...stakeContract,
                    functionName: 'tokensStaked',
                    chainId: environment.eth.chainIdInt, // 9
                    args: [],
                },
                // {
                //     ...presaleContractEth,
                //     functionName: 'checkReferralStatus', // this method is returning the referral status on ETH network.
                //     chainId: environment.eth.chainIdInt, // 10
                //     args: [this.walletAddress || '0x0000000000000000000000000000000000000000'],
                // },
            ],
        } as const;

        const presaleData: any = await readContracts(this.wagmiConfig, smartContract);

        if (this.walletAddress) {
            ethBalanceData.nativeBalETH = +formatEther(presaleData[4]?.result || 0);
            ethBalanceData.usdtBalETH = +formatUnits(presaleData[5].result, 6);
            ethBalanceData.usdcBalETH = +formatUnits(presaleData[7].result, 6);
        } else {
            ethBalanceData.usdtBalETH = 0;
            ethBalanceData.nativeBalETH = 0;
            ethBalanceData.usdcBalETH = 0;
        }

        /* assigning the value of no of token sold va bsc and eth contract */
        ethBalanceData.maxTokensToBuyEVM = Number(presaleData[2].result);
        // ethBalanceData.isReferalAvailable = !!Number(presaleData[10].result);
        this.saleToken = presaleData[6].result

        /* assigning the value of total USDT raised via eth bsc and poly */
        try {
            ethBalanceData.buyHelprETH = +formatEther(presaleData[0].result); // no of app tokens in exchange of 1 eth
            ethBalanceData.usdtBuyHelperETH = Number(presaleData[1].result); // no of app tokens in exchange of 1 Eth USDT
        } catch {
            // Buy-helper call fails when presale is over — reset to zero to prevent stale values
            ethBalanceData.buyHelprETH = 0;
            ethBalanceData.usdtBuyHelperETH = 0;
        }

        /* assigning the value of total token purchased by the current user */
        ethBalanceData.ethClaimable = +formatEther(presaleData[3].result || 0);
        this.userStaked = +formatEther(presaleData[8].result[0] || 0);
        const totalStaked = +formatEther(presaleData[9].result || 0);
        this.stakeApy = +((+environment.apyCalcConst / totalStaked) * 100).toFixed(0);
        return Promise.resolve(ethBalanceData)
    }

    async prepareBSCContractData(): Promise<IBalnceDataEVM> {
        const bscBalanceData: IBalnceDataEVM = {} as IBalnceDataEVM
        const smartContract = {
            contracts: [

                {
                    ...presaleContractBsc,
                    functionName: 'bnbBuyHelper', // this method is returning the amount of bnb required for 1 app token.
                    chainId: environment.bsc.chainIdInt, //0
                    args: [1],
                },
                {
                    ...presaleContractBsc,
                    functionName: 'usdtBuyHelper', // this method is returning the amount of usdt required for 1 app token.
                    chainId: environment.bsc.chainIdInt, // 1
                    args: [1],
                },
                {
                    ...presaleContractBsc,
                    functionName: 'userDeposits', // this method is returning the total token purchased by current user via BSC network.
                    chainId: environment.bsc.chainIdInt, // 2
                    args: [this.walletAddress || '0x0000000000000000000000000000000000000000'],
                },
                {
                    ...multicallBscContract,
                    functionName: 'getEthBalance', // this method is returning the Bnb balance of user via multicall contract.
                    chainId: environment.bsc.chainIdInt,  //3
                    args: [this.walletAddress || '0x0000000000000000000000000000000000000000'],
                },
                {
                    ...bscUsdtContract,
                    functionName: 'balanceOf', // this method is returning the usdt balance on BSC network.
                    chainId: environment.bsc.chainIdInt, //4
                    args: [this.walletAddress || '0x0000000000000000000000000000000000000000'],
                },
                {
                    ...bscUsdcContract,
                    functionName: 'balanceOf', // this method is returning the usdc balance on BSC network.
                    chainId: environment.bsc.chainIdInt, // 5
                    args: [this.walletAddress || '0x0000000000000000000000000000000000000000'],
                },
                // Referral
                // {
                //     ...presaleContractBsc,
                //     functionName: 'checkReferralStatus', // this method is returning the referral status on BSC network.
                //     chainId: environment.bsc.chainIdInt, // 6
                //     args: [this.walletAddress || '0x0000000000000000000000000000000000000000'],
                // },
            ],
        } as const;

        const presaleData: any = await readContracts(this.wagmiConfig, smartContract)

        if (this.walletAddress) {
            bscBalanceData.nativeBalBSC = +formatEther(presaleData[3]?.result || 0);
            bscBalanceData.usdtBalBSC = +formatUnits(presaleData[4].result, 18);
            bscBalanceData.usdcBalBSC = +formatUnits(presaleData[5].result, 18);
        } else {
            bscBalanceData.usdtBalBSC = 0;
            bscBalanceData.nativeBalBSC = 0;
            bscBalanceData.usdcBalBSC = 0;
        }
        // bscBalanceData.isReferalAvailable = !!Number(presaleData[6].result);

        /* assigning the value of no of token sold va bsc and eth contract */
        /* assigning the value of total USDT raised via eth bsc and poly */
        try {
            bscBalanceData.buyHelperBSC = +formatEther(presaleData[0].result); // no of app tokens in exchange of 1 eth
            bscBalanceData.usdtBuyHelperBSC = Number(presaleData[1].result); // no of app tokens in exchange of 1 Eth USDT
        } catch {
            // Buy-helper call fails when presale is over — reset to zero to prevent stale values
            bscBalanceData.buyHelperBSC = 0;
            bscBalanceData.usdtBuyHelperBSC = 0;
        }

        /* assigning the value of total token purchased by the current user */
        bscBalanceData.bscClaimable = +formatEther(presaleData[2].result || 0);
        return Promise.resolve(bscBalanceData)
    }

    async prepareTokenomics(presaleData: any, currentStep: number, solUnsoldArr: number[] = []): Promise<ITokenomics[]> {
        const skipIndexEth: Array<any> = [];
        const skipIndexBsc: Array<any> = [];

        const ethUnsoldArr = skipIndexUpdate(presaleData[3].result, skipIndexEth);
        // ethUnsoldArr.splice(<index>, 0, <new value>);
        const bnbUnsoldArr = skipIndexUpdate(presaleData[4].result, skipIndexBsc);
        let tokenomics: ITokenomics[] = [];
        const tokenomics2 = [];
        const _isSolanaActivated = isSolanaActivated(this.reownService.walletConnector.chainNamespaces)
        let newCounter = 0;
        for (let time of presaleData[5].result) {
            let minToken = newCounter == 0 ? 0 : Number(presaleData[6].result[newCounter - 1]);
            let maxToken = Number(presaleData[6].result[newCounter]);
            let tokenUSDT = +formatEther(presaleData[7].result[newCounter]);
            let roundInfo: ITokenomics = {
                minToken,
                maxToken,
                maxAmount: newCounter == 0 ? (maxToken - minToken) * tokenUSDT : (maxToken - minToken) * tokenUSDT + tokenomics2[newCounter - 1].maxAmount,
                tokenUSDT,
                title: 'widget.round_title',
                endDate: Number(time) * 1000,
            };
            if (newCounter < currentStep) {
                const ethVal = ethUnsoldArr[newCounter] || 0;
                const bscVal = bnbUnsoldArr[newCounter] || 0;
                const solVal = solUnsoldArr[newCounter] || 0;
                // if SOL chain is not enabled then do it 1 * {roundUnsoldCode}
                let roundUnSold = (_isSolanaActivated ? 2 : 1) * (roundInfo?.maxToken - roundInfo?.minToken) - (ethVal + bscVal + solVal);
                roundUnSold = roundUnSold * roundInfo.tokenUSDT;
                roundInfo.maxAmount = roundInfo.maxAmount + roundUnSold;
            }
            // roundInfo.maxAmount = Math.ceil(roundInfo.maxAmount)
            tokenomics2.push(roundInfo);
            newCounter++;
        }
        tokenomics = tokenomics2;
        tokenomics = [...tokenomics, tokenomics[tokenomics.length - 1]]; // cloned 
        return Promise.resolve(tokenomics)
    }

    setData(): void {
        if (typeof window === 'undefined') return;
        const wagmiData = getAccount(this.wagmiConfig);
        this.chainId = wagmiData.chainId as number;
        this.refreshRate = wagmiData.address ? 10000 : 20000;
        setTimeout(() => {
            if (wagmiData.address && wagmiData.address != this.oldWalletAddress) {
                this.oldWalletAddress = wagmiData.address;
                this.analyticsService.sendDashFx(this.walletAddress);
                this.analyticsService.windowDataLayer('connectWallet', 'successful', 2, 1, 0, '', '', 0, 0, this.walletAddress, "", this.balanceDataEVM, this.chainId, this.tokenomics[this.balanceDataEVM.currentStep].tokenUSDT);
            }
        }, 2000);
        if (environment.production) {
            this.calculateGas();
            this.calculateGas(false)
        }
    }

    /**
  * switching the current network. If user is on wrong network
  * and got error on switching then a popup
  * will appear saying to connect correct chain
  */
    async switchNetwork(chainId: number): Promise<void> {
         console.log('switchNetwork called', this.chainId, chainId)
        if (this.chainId == chainId) return;
        else {
        const oldChainId = this.chainId;
        const newChainId = chainId;
        const newNetwork:any = environment.production ?
                (chainId === environment.eth.chainIdInt ? mainnet : bsc) :
                (chainId === environment.eth.chainIdInt ? sepolia : bscTestnet);
        this.analyticsService.vwoData('networkSwitch', { networkSwitchInitiated: true, networkSwitchCompleted: false, oldChainId, newChainId });

         await this.reownService.walletConnector.switchNetwork(newNetwork).then((success: any) => {
                console.log(success, "switchNetwork success");
                this.chainId = chainId;
                this.analyticsService.vwoData('networkSwitch', { networkSwitchInitiated: false, networkSwitchCompleted: true, oldChainId, newChainId });
            }).catch((error: any) => {
                console.log(error, "switchNetwork error");
                const chain = chainId == environment.eth.chainIdInt ? environment.eth : environment.bsc;
                const chainName = chain.chainInfo.params[0].chainName;
                this.popupService.messagePopup({ icon: 'info', desc: this.translateService.instant('popup.switchNetwork', { chain_name: chainName }), confirmBtnTxt: this.translateService.instant('misc.ok') });
            })

       /* await switchChain(this.wagmiConfig, {
            chainId: newNetwork,
        }).then(
            (success: any) => {
                // this.appRef.tick();
                this.chainId = chainId;
                this.analyticsService.vwoData('networkSwitch', { networkSwitchInitiated: false, networkSwitchCompleted: true, oldChainId, newChainId });
            },
            (err: any) => {
                const chain = chainId == environment.eth.chainIdInt ? environment.eth : environment.bsc;
                const chainName = chain.chainInfo.params[0].chainName;
                this.popupService.messagePopup({ icon: 'info', desc: this.translateService.instant('popup.switchNetwork', { chain_name: chainName }), confirmBtnTxt: this.translateService.instant('misc.ok') });
            },
        );*/
        }
    }


    // SOL BALANCE AREA // 

    async getPresaleDataSOL(): Promise<IBalnceDataSOL> {
        const _balanceData: IBalnceDataSOL = {} as IBalnceDataSOL;
        try {
            let _balanceDataConnected: IBalnceDataSOL = {} as IBalnceDataSOL;
            // const accountInfo = await this.solService.program.account.presaleAccount.fetch(this.solService.presaleAccountAddress[0]); // this.presaleAccountAddress[0] = Public Key for Presale Account Address
            const stateInfo = await this.solService.program.account.stateInfo.fetch(this.solService.stateInfoAddress[0]); // this.stateInfoAddress[0] = Public Key for State Info

            // // paymentWalletUsdtAccount - The SPL token account that receives USDT payments
            // // paymentWalletSolAccount -	A wallet address (public key) that receives SOL payments
            // this.paymentWalletUsdtAccount = accountInfo.paymentWalletUsdtAccount;
            // this.paymentWalletUsdcAccount = accountInfo.paymentWalletUsdcAccount;
            // this.paymentWalletSolAccount = accountInfo.paymentWallet;
            _balanceData.paused = stateInfo.pause;
            _balanceData.currentStep = stateInfo.currentTier;
            _balanceData.totalTokensSoldSOL = Number(stateInfo.tokensSold) + 1;
            const solUsdtRaised = Number(stateInfo.totalUsdRaised) / 1000000 + 1;
            _balanceData.usdRaised = solUsdtRaised;

            _balanceData.tokenBuyLimit = Number(stateInfo.maxTokensToSell);
            const skipIndexSol: Array<any> = [];
            const soldArray = stateInfo?.tokensLeftUnsold?.toString()?.split(',')
            const solUnsoldArr = skipIndexUpdate(
                soldArray.map((val: any) => Number(val)),
                skipIndexSol,
            );
            _balanceData.unsoldArr = solUnsoldArr;

            if (this.walletAddressSOL) {
                _balanceDataConnected = await this.getMetaDataSOL(_balanceData);
            }
            this.estimateTotalFeeSol()
            return Promise.resolve({ ..._balanceData, ..._balanceDataConnected })
        } catch {
            return Promise.resolve(_balanceData);
        }

    }

    /**
   * Get the USDT raised value from Lambda
   */

    getSolprice(): Promise<any> {
        return this.http.get('https://api.diadata.org/v1/assetQuotation/Solana/0x0000000000000000000000000000000000000000').toPromise();
    }

    /**
   * Get the metadata of the connected user
   */
    async getMetaDataSOL(balanceData: IBalnceDataSOL): Promise<IBalnceDataSOL> {
        // === get SOL balance ===== 1 SOL = 1,000,000,000 lamports, this.lamport_per_sol = 1_000_000_000
        balanceData.nativeBalSol = Number(await this.solService.connection.getBalance(this.solService.solWalletPubkey)) / this.solService.lamports_per_sol;

        // === get USDT balance =====

        try {
            // <---------------       USDC BALANCE         -------------------------->
            // getAssociatedTokenAddressSync -> Gets Associated Token Account Address of USDC for that specific User
            const usdcTokenAccount = await getAssociatedTokenAddressSync(this.solService.USDC_TOKEN_ADDRESS, this.solService.solWalletPubkey, true, TOKEN_PROGRAM_ID, this.solService.associate_programId);
            const usdcAccountInfo = await this.solService.connection.getTokenAccountBalance(usdcTokenAccount);
            balanceData.usdcBalSol = usdcAccountInfo.value.uiAmount || 0;
        } catch (e) {
            balanceData.usdcBalSol = 0;
        }

        try {
            // <---------------       USDT BALANCE         -------------------------->
            const usdtTokenAccount = await getAssociatedTokenAddressSync(this.solService.USDT_TOKEN_ADDRESS, this.solService.solWalletPubkey, true, TOKEN_PROGRAM_ID, this.solService.associate_programId);
            const usdtAccountInfo = await this.solService.connection.getTokenAccountBalance(usdtTokenAccount);
            balanceData.usdtBalSol = usdtAccountInfo.value.uiAmount || 0;
        } catch (e) {
            balanceData.usdtBalSol = 0;
        }

        // this.web3PublicKey.findProgramAddressSync returns [Public Key, Size]
        const userInfoAddress = this.solService.web3PublicKey.findProgramAddressSync([this.solService.solWalletPubkey.toBuffer()], this.solService.programId);
        let userInfo;

        try {
            userInfo = await this.solService.program.account.userInfo.fetch(userInfoAddress[0]);
        } catch (e) {
            // console.log("Presale over.", e);
            userInfo = null;
        }
        //  balanceData.userClaimable  = userInfo ? Number(userInfo.value) / LAMPORTS_PER_SOL : 0;
        balanceData.userClaimableSOL = userInfo ? Number(userInfo.value) : 0;
        return Promise.resolve(balanceData)
    }


    // Multi connection Item
    connectWallet(chain: "SOL" | "ETH" | "" = "") {
        this.reownService.connectWallet(chain)
    }

    disconnectWallet() {
        this.reownService.disconnectWallet()
    }

    async estimateTotalFeeSol(): Promise<number> {
        // --- 1. Estimate Required Compute Units (CU) via Simulation ---
        const latestBlockhash = await this.solService.connection.getLatestBlockhash();
        const accountInfo: any = await this.solService.program.account.presaleAccount.fetch(this.solService.presaleAccountAddress[0]); // this.presaleAccountAddress[0] = Public Key for Presale Account Address
        const walletPubkey = new this.solService.web3PublicKey(environment.solGasAddress)
        const userInfoAddress = this.solService.web3PublicKey.findProgramAddressSync([walletPubkey.toBuffer()], this.solService.programId);
        const transactionObj = await this.solService.program.transaction.buyToken(new this.solService.BN(10), {
            accounts: {
                presaleAccount: this.solService.presaleAccountAddress[0],
                stateInfo: this.solService.stateInfoAddress[0],
                userInfo: userInfoAddress[0],
                signer: walletPubkey, //this.provider.wallet.publicKey,
                // pythPriceFeed: new this.solanaWeb3.this.web3PublicKey(environment.pythPriceFeed),
                paymentWallet: accountInfo.paymentWallet,
                systemProgram: this.solService.systemProgram.programId,
                otherAccount: new this.solService.web3PublicKey(environment.sol.otherAccountSol),
                chainlinkFeed: new this.solService.web3PublicKey(environment.sol.chainlinkFeed),
                chainlinkProgram: new this.solService.web3PublicKey(environment.sol.chainlinkProgram),
            },
        });
        transactionObj.feePayer = walletPubkey;
        transactionObj.recentBlockhash = latestBlockhash.blockhash;

        // Set a temporary fee payer for simulation if not already set
        if (!transactionObj.feePayer) {
            // Fee payer required for simulation — caller must set this before calling estimateTotalFeeSol
        }

        const simulationResult: any = await this.solService.connection.simulateTransaction(transactionObj);

        if (simulationResult.value.err) {
            throw new Error("Transaction simulation failed: " + JSON.stringify(simulationResult.value.err));
        }

        // CUs consumed from simulation + a small buffer
        const estimatedCU = Math.ceil(simulationResult?.value?.unitsConsumed * 1.3); // 30% buffer

        // --- 2. Estimate Competitive Compute Unit Price (CU Price) ---
        // Look up recent fees for the main program accounts involved for a "local fee market" estimate
        // Map the keys to their Public Key objects, not their string representation
        const accountPubKeys: any[] = transactionObj.instructions.flatMap((ix: any) =>
            ix.keys.map((k: any) => k.pubkey)
        );        // Filter out duplicates based on the string representation, but keep the object reference
        const uniqueKeysMap = new Map();
        accountPubKeys.forEach((pubkey) => {
            // Only add it if it hasn't been added yet (by its Base58 string)
            if (!uniqueKeysMap.has(pubkey.toBase58())) {
                uniqueKeysMap.set(pubkey.toBase58(), pubkey);
            }
        });

        // Convert the Map values back into an array of Public Key objects
        const uniquePubKeys = Array.from(uniqueKeysMap.values());

        // Fetch recent fees
        const feeHistory = await this.solService.connection.getRecentPrioritizationFees({ lockedWritableAccounts: uniquePubKeys });

        // Choose the median or a high percentile fee from recent successful transactions
        const paidFees = feeHistory
            .filter(fee => fee.prioritizationFee > 0)
            .map(fee => fee.prioritizationFee)
            .sort((a, b) => a - b); // Sort to find median

        let cuPriceMicroLamports = 0; // Default to zero if no one is paying priority fee
        if (paidFees.length > 0) {
            // Use the median fee as a good balance for inclusion
            const medianIndex = Math.floor(paidFees.length / 2);
            cuPriceMicroLamports = paidFees[medianIndex];
        }

        // --- 3. Calculate Final Estimated Fee ---

        // A. Calculate Prioritization Fee (in Lamports)
        // 1 Lamport = 1,000,000 Micro-Lamports
        const priorityFeeLamports = (estimatedCU * cuPriceMicroLamports) / 1_000_000;

        // B. Calculate Base Fee (in Lamports)
        // Solana base fee is currently 5000 Lamports per signature.
        // Assuming 1 signature for a simple dApp transaction (yours and the fee payer's signature is the same in most cases).
        const BASE_FEE_PER_SIGNATURE = 5000;
        const numSignatures = transactionObj.signatures.length || 1; // Use actual or default to 1
        const baseFeeLamports = numSignatures * BASE_FEE_PER_SIGNATURE;

        // C. Total Estimated Fee
        const totalFeeLamports = Math.ceil(baseFeeLamports + priorityFeeLamports);
        const feesInDecimal = totalFeeLamports / this.solService.lamports_per_sol;
        return totalFeeLamports;
    }


    /**
     * Function to calculate the gas fee for transaction.
     * this function convert the USDT value to app token
     * @param amount: Amount to spend
     * @returns : converted no of app tokens
     */

    async calculateGas(isEth = true) {
        try {

            const publicClient = getPublicClient(this.wagmiConfig);
            if (!publicClient) {
                this.refreshIdGas = setTimeout(() => this.calculateGas(isEth), this.refreshRateGas);
                return;
            }
            let chainId = isEth ? environment.eth.chainIdInt : environment.bsc.chainIdInt;
            const presaleAdd = isEth ? environment.eth.presaleAddress : environment.bsc.presaleAddress;

            // Step 1: Prepare contract config
            const contractConfig = {
                ...getPresaleContract(chainId),
                functionName: getChainData(chainId).nativeFunction,
                chainId: chainId,
                args: getChainData(chainId).hasStaking ? [1000, true] : [1000],
                value: parseEther(`${(this.balanceDataEVM.buyHelprETH || 1) * 1000}`),
            };

            const nativeCallData = encodeFunctionData(contractConfig);


            // Step 2: Estimate gas limit
            const estimatedGas = await estimateGas(this.wagmiConfig, {
                data: nativeCallData,
                account: environment.gasAddress as any,
                to: presaleAdd as Address,
                value: parseEther("0.1"),
            });

            // Step 3: Apply 1.3x buffer like MetaMask //Optional
            // const bufferedGas = estimatedGas;
            const bufferedGas = (estimatedGas * 15n) / 10n;

            // Step 4: Get base fee from current block
            const block = await publicClient.getBlock();
            const baseFeePerGas = block["baseFeePerGas"] ?? 0n;

            // Step 5: Get priority fee estimate
            const { maxPriorityFeePerGas = 3_000_000_000n } =
                await publicClient.estimateFeesPerGas();  // 1 Gwei minimum

            const adjustedPriority =
                maxPriorityFeePerGas < 3_000_000_000n
                    ? 3_000_000_000n
                    : maxPriorityFeePerGas;     // 1 Gwei minimum
            const maxFeePerGas = baseFeePerGas + adjustedPriority;
            // const adjustedMaxFee =  applyBuyGasBuffer(maxFeePerGas)

            // Step 6: Final gas cost in ETH
            const gasCostWei = bufferedGas * maxFeePerGas;
            const gasCostEth = formatEther(gasCostWei);

            if (isEth)
                this.gasFeesEth = +gasCostEth;
            else
                this.gasFeesBsc = +gasCostEth;
            if (this.refreshIdGas) clearTimeout(this.refreshIdGas);
            this.refreshIdGas = setTimeout(() => this.calculateGas(isEth), this.refreshRateGas);

        } catch {
            // Gas estimation failed — retry on next interval
            this.refreshIdGas = setTimeout(() => this.calculateGas(isEth), this.refreshRateGas);
        }
        // Call the self method on each 5secs interval
    }

    get gasFees() {
        if (this.chainId === environment.eth.chainIdInt) {
        return this.gasFeesEth;
        } else if (this.chainId === environment.bsc.chainIdInt) {
        return this.gasFeesBsc;
        }
        return 0;
    }

        /**
    * get App token balance from the user's wallet.
    * This is for further stake from wallet balance
    */

    async getMetaData() {
        const tokenBalance = await getBalance(this.wagmiConfig, {
            address: this.walletAddress as any,
            token: this.saleToken as any,
            chainId: getChainData(this.chainId).chainIdInt,
        });

        /* user's Eth/Bnb and USDT balance */
        this.stakeService.updateUserBal(+formatUnits(tokenBalance.value, tokenBalance.decimals));
    }
}
